import React, { Fragment, useState, useEffect } from "react";
import { useParams } from "wouter";
import { cloneDeep } from "lodash";
import { gql, useClient, useQuery } from "urql";
import { Link } from "wouter";

import { Button } from "../../../../components/src/Button";
import { TooltipLabel } from "../../../../components/src/TooltipLabel";
import { Grid, Column } from "../../../../components/src";
import { numberFormatFun } from "../../../../common/src/utils/FormatFunctions";

import { usePrevious } from "../../../../components/src/common/Utils";
import { IssuerTypeEnum, TradingManagerColumn, InstrumentModelTypeEnum, TransactionStatus } from "../../types.generated";
import { DateForm } from "../../../../components/src/dateForm";
import { Alert } from "../../../../components/src/Alert";
import { calculateDivisor, cdsNotionalPortfolioCcy, isHighYieldCdsIndex } from "./cdsExposure";
import { nettedOrdersInBaseCurrency, nettedOrderValues, toWholeMillionsRoundedDown } from "./cashLadderOrders";
import { getFundOrders } from "./queries";

const getTradingmanager = gql`
    query getTradingmanager($clientIds: [GraphQLObjectId!], $endDate: GraphQLDateString, $lookThrough: Boolean) {
        tradingmanager(filterZeroPositions: true, filter: { endDate: $endDate, clientIds: $clientIds }, lookThrough: $lookThrough) {
            name
            isin
            quantity
            instrumentId
            localExposure
            exposure
            issuerName
            issuerId
            issuerType
            bloombergTicker
            modelType
            maturityDate
            fxRate
            currency
            baseCurrency
            instrument {
                model {
                    legs {
                        notional
                    }
                }
                description
            }
            swaplab2Results(date: $endDate) {
                interestRateYieldDelta
                creditYieldDelta
            }
        }
    }
`;

interface IssuerExposure {
    issuer: string;
    issuerType: IssuerTypeEnum;
    issuerId: string;
    exposure: number;
    percent: number;
    subscRed: number;
    sumRow: boolean;
}

interface ModelTypeExposure {
    modelType: string;
    exposure: number;
    percent: number;
}

interface CurrencyExposure {
    currency: string;
    percentTotal: number;
    localExposure: number;
    //exposure: number;
    percentAssets: number;
    cash: number;
    percentCash: number;
}

interface CdsExposure {
    name: string;
    instrumentId: string;
    currency: string;
    fxRate: number;
    notional: number;
    divisor: number;
    notionalPortfolioCcy: number;
    ratioToNav: number;
}

interface EsgExposure {
    cdsName: string;
    cdsInstrumentId: string;
    cdsPosition: number;

    hedgeName: string;
    hedgeInstrumentId: string;
    hedgePosition: number;
    hedgeNotional: number;

    currency: string;
    dirtyNoNames: number;
    indexNoNames: number;
    grossDirtyExposure: number;
    netDirtyExposure: number;
}

const check_today_date = (endDate: string) => {
    const today = new Date().toISOString().slice(0, 10);
    if (endDate === today) {
        return true;
    } else {
        return false;
    }
};

const sumAll = (data: TradingManagerColumn[]) => {
    return data.reduce(function (prev, cur) {
        return prev + cur.exposure;
    }, 0);
};

// Sum for each issuer
const getIssuerExposure = (data: TradingManagerColumn[]) => {
    const issuerExposure: IssuerExposure[] = [];

    const sumFxExposure = function (tradingmanager: TradingManagerColumn[], issuer_sum: string) {
        return tradingmanager
            .filter(({ issuerName }) => issuerName === issuer_sum)
            .reduce(function (prev, cur) {
                return prev + cur.exposure;
            }, 0);
    };

    const nav = sumAll(data);

    let sumOver5Exposure = 0;
    const uniqueIssuersSorted = [...new Set(data.map(({ issuerName }) => issuerName))].sort();
    uniqueIssuersSorted.forEach((issuerName) => {
        const sumTemp: number = sumFxExposure(data, issuerName);
        const tempItem: TradingManagerColumn[] = data.filter((obj) => {
            return obj.issuerName === issuerName;
        });
        issuerExposure.push({
            issuer: issuerName,
            issuerId: tempItem[0].issuerId,
            issuerType: tempItem[0].issuerType,
            exposure: sumTemp,
            percent: sumTemp / nav,
            subscRed: null,
            sumRow: false
        });
        if (
            sumTemp / nav > 0.05 &&
            tempItem[0].issuerType !== IssuerTypeEnum.Government &&
            issuerName !== null &&
            tempItem[0].issuerId !== "5c46ff650a40e100016db4d4" //"IHS Markit Benchmark Administration Limited"
        ) {
            sumOver5Exposure = sumOver5Exposure + sumTemp;
        }
    });
    issuerExposure.push({
        issuer: "Sum over 5% ex gov, markit",
        issuerId: null,
        issuerType: null,
        exposure: sumOver5Exposure,
        percent: sumOver5Exposure / nav,
        subscRed: null,
        sumRow: true
    });
    return issuerExposure;
};

// Sum for model type
const getModelTypeExposure = (data: TradingManagerColumn[]) => {
    const modeTypeExposure: ModelTypeExposure[] = [];

    const sumModelTypeExposure = function (tradingmanager: TradingManagerColumn[], modelTypeSum: string) {
        return tradingmanager
            .filter(({ modelType }) => modelType === modelTypeSum)
            .reduce(function (prev, cur) {
                return prev + cur.exposure;
            }, 0);
    };

    const nav = sumAll(data);

    const uniqueModelTypesSorted = [...new Set(data.map(({ modelType }) => modelType))].sort();
    uniqueModelTypesSorted.forEach((modelType) => {
        const sumTemp: number = sumModelTypeExposure(data, modelType);
        const tempItem: TradingManagerColumn[] = data.filter((obj) => {
            return obj.modelType === modelType;
        });
        modeTypeExposure.push({ modelType: tempItem[0].modelType, exposure: sumTemp, percent: sumTemp / nav });
    });
    return modeTypeExposure;
};

// Sum for currency
const getCurrencyExposure = (data: TradingManagerColumn[]) => {
    const currencyExposure: CurrencyExposure[] = [];
    const cashModel = InstrumentModelTypeEnum.Balance;

    const sumCurrencyExposure = function (tradingmanager: TradingManagerColumn[], currencySum: string) {
        return tradingmanager
            .filter(({ currency }) => currency === currencySum)
            .filter(({ modelType }) => modelType !== cashModel)
            .reduce(function (prev, cur) {
                return prev + cur.exposure;
            }, 0);
    };

    const sumLocalCurrencyExposure = function (tradingmanager: TradingManagerColumn[], currencySum: string) {
        return tradingmanager
            .filter(({ currency }) => currency === currencySum)
            .filter(({ modelType }) => modelType !== cashModel)
            .reduce(function (prev, cur) {
                return prev + cur.localExposure;
            }, 0);
    };

    const sumCurrencyExposureCash = function (tradingmanager: TradingManagerColumn[], currencySum: string) {
        return tradingmanager
            .filter(({ currency }) => currency === currencySum)
            .filter(({ modelType }) => modelType === cashModel)
            .reduce(function (prev, cur) {
                return prev + cur.exposure;
            }, 0);
    };

    const sumLocalCurrencyExposureCash = function (tradingmanager: TradingManagerColumn[], currencySum: string) {
        return tradingmanager
            .filter(({ currency }) => currency === currencySum)
            .filter(({ modelType }) => modelType === cashModel)
            .reduce(function (prev, cur) {
                return prev + cur.localExposure;
            }, 0);
    };

    const nav = sumAll(data);

    const uniqueCurrenciesSorted = [...new Set(data.map(({ currency }) => currency))].sort();
    uniqueCurrenciesSorted.forEach((currency) => {
        const sumTemp: number = sumCurrencyExposure(data, currency);
        const sumTempLocal: number = sumLocalCurrencyExposure(data, currency);
        const sumTempCash: number = sumLocalCurrencyExposureCash(data, currency);
        const sumTempLocalCash: number = sumCurrencyExposureCash(data, currency);

        const tempItem: TradingManagerColumn[] = data.filter((obj) => {
            return obj.currency === currency;
        });
        currencyExposure.push({
            currency: tempItem[0].currency,
            percentTotal: (sumTemp + sumTempLocalCash) / nav,
            localExposure: sumTempLocal,
            //exposure: sumTemp,
            percentAssets: sumTemp / nav,
            cash: sumTempCash,
            percentCash: sumTempLocalCash / nav
        });
    });
    return currencyExposure;
};

const getcdsIndexExposure = (data: TradingManagerColumn[], referenceTenor: number) => {
    const cdsExposure: CdsExposure[] = [];
    const esgExposure: EsgExposure[] = [];

    const nav = sumAll(data);

    let temp_sum_notional = 0;
    data.forEach((item) => {
        let igHyFlag;
        if (isHighYieldCdsIndex(item.name)) {
            igHyFlag = "HY";
        } else {
            igHyFlag = "IG";
        }
        const hedgeItem = data.find(
            (x) =>
                x.modelType === InstrumentModelTypeEnum.CdsBasket && x.currency === item.currency && x.name.includes("_" + igHyFlag + "_")
        );

        if (item.modelType == InstrumentModelTypeEnum.CdsIndex) {
            const divisorNotional = calculateDivisor(item.maturityDate, referenceTenor);
            const notionalPortfolioCcy = cdsNotionalPortfolioCcy(item, referenceTenor);
            temp_sum_notional = temp_sum_notional + notionalPortfolioCcy;
            cdsExposure.push({
                name: item.name,
                instrumentId: item.instrumentId,
                currency: item.currency,
                fxRate: item.fxRate,
                notional: item.quantity,
                divisor: divisorNotional,
                notionalPortfolioCcy: notionalPortfolioCcy,
                ratioToNav: notionalPortfolioCcy / nav
            });

            let hedgeNotional = null;
            let cdsHoldings = null;
            let indexNoNames = null;
            let grossDirtyExposure = null;
            let hedgeItemName = null;
            let hedgeItemInstrumentId = null;
            let hedgeItemQuantity = null;
            let netDirtyExposure = null;

            if (typeof hedgeItem !== "undefined") {
                const parseString = "{" + hedgeItem.instrument.description + "}";
                const instr_des = JSON.parse(parseString);
                grossDirtyExposure = (instr_des["cdsHoldings"] / instr_des["indexHoldings"]) * item.quantity;
                hedgeNotional = hedgeItem.quantity * hedgeItem.instrument.model.legs[0].notional;
                cdsHoldings = instr_des["cdsHoldings"];
                indexNoNames = instr_des["indexHoldings"];
                hedgeItemName = hedgeItem.name;
                hedgeItemInstrumentId = hedgeItem.instrumentId;
                hedgeItemQuantity = hedgeItem.quantity;
                netDirtyExposure = grossDirtyExposure - hedgeNotional;
            } else {
                hedgeItemName = "No hedge found";
            }

            esgExposure.push({
                cdsName: item.name,
                cdsInstrumentId: item.instrumentId,
                currency: item.currency,
                cdsPosition: item.quantity,

                hedgeName: hedgeItemName,
                hedgeInstrumentId: hedgeItemInstrumentId,
                hedgePosition: hedgeItemQuantity,
                hedgeNotional: hedgeNotional,

                dirtyNoNames: cdsHoldings,
                indexNoNames: indexNoNames,

                grossDirtyExposure: grossDirtyExposure,
                netDirtyExposure: netDirtyExposure
            });
        } else if (item.modelType == InstrumentModelTypeEnum.CdsBasket) {
            const divisorNotional = calculateDivisor(item.maturityDate, referenceTenor);
            const ntl_temp = item.instrument.model.legs[0].notional;
            const notionalPortfolioCcy = cdsNotionalPortfolioCcy(item, referenceTenor);
            temp_sum_notional = temp_sum_notional + notionalPortfolioCcy;
            cdsExposure.push({
                name: item.name,
                instrumentId: item.instrumentId,
                currency: item.currency,
                fxRate: item.fxRate,
                notional: -item.quantity * ntl_temp,
                divisor: divisorNotional,
                notionalPortfolioCcy: notionalPortfolioCcy,
                ratioToNav: notionalPortfolioCcy / nav
            });
        }
    });
    const baseCurrency = data.length === 0 ? null : data[0].baseCurrency;
    cdsExposure.push({
        name: "SUM",
        instrumentId: null,
        currency: baseCurrency,
        fxRate: null,
        notional: null,
        divisor: nav,
        notionalPortfolioCcy: temp_sum_notional,
        ratioToNav: temp_sum_notional / nav
    });
    return [cdsExposure, esgExposure];
};

const calculateCdsExposurePreview = (
    cdsExposure: CdsExposure[] | EsgExposure[],
    newTrades: number[],
    subscrValue: number,
    esgExposure: CdsExposure[] | EsgExposure[]
): CdsExposure[] => {
    // calculate CdsExposure trade preview
    const sumItem: CdsExposure = cdsExposure.find((x: CdsExposure) => x.name === "SUM") as CdsExposure;

    // early calculate divisor, since 'ratioToNav' depend on new SUM.Divisor
    const divisor = subscrValue ? sumItem.divisor + subscrValue * 1000000 : sumItem.divisor;

    const cdsExposurePreview = cdsExposure.map((item, index) => {
        const preItem = cloneDeep(item);
        if (newTrades.length > 0 && newTrades[index]) {
            const newTrade = newTrades[index];
            preItem.newTrade = newTrade;
            preItem.notional = preItem.notional + newTrade * 1000000;
            preItem.notionalPortfolioCcy = (preItem.notional * preItem.fxRate) / preItem.divisor;
        }
        preItem.ratioToNav = sumItem ? preItem.notionalPortfolioCcy / divisor : null;

        // EsgExposure.cdsPosition update (matched on the CDS index instrument)
        const matchingEsg: EsgExposure = esgExposure.find((esg: EsgExposure) => esg.cdsInstrumentId == preItem.instrumentId) as EsgExposure;

        if (matchingEsg) {
            matchingEsg.cdsPosition = preItem.notional;
            matchingEsg.grossDirtyExposure = (matchingEsg.dirtyNoNames / matchingEsg.indexNoNames) * preItem.notional;
            matchingEsg.netDirtyExposure = matchingEsg.grossDirtyExposure - matchingEsg.hedgeNotional;
        }

        // EsgExposure.hedgeNotional update (matched on the hedge/CdsBasket instrument).
        // The CDS Exposure notional for a hedge is -(quantity * legNotional), i.e. -hedgeNotional,
        // so the hedge notional shown in the ESG table is the negated exposure notional.
        const matchingEsgHedge: EsgExposure = esgExposure.find(
            (esg: EsgExposure) => esg.hedgeInstrumentId == preItem.instrumentId
        ) as EsgExposure;

        if (matchingEsgHedge) {
            matchingEsgHedge.hedgeNotional = -preItem.notional;
            matchingEsgHedge.netDirtyExposure = matchingEsgHedge.grossDirtyExposure - matchingEsgHedge.hedgeNotional;
        }

        return preItem;
    });

    // calculate SUM row
    const sumRow: CdsExposure = cdsExposurePreview.find((x: CdsExposure) => x.name === "SUM") as CdsExposure;
    // In Divisor column it should be previous value + value in column New trade (M) on SUM row.
    sumRow.divisor = divisor;
    // In Ntl Port ccy column in SUM row should be sum of all values in the Ntl Port ccy column.
    sumRow.notionalPortfolioCcy = cdsExposurePreview
        .filter((x: CdsExposure) => x.name !== "SUM")
        .reduce((acc, item: CdsExposure) => acc + item.notionalPortfolioCcy, 0);
    // In Ratio to nav column in SUM row should be value in Ntl Port ccy on SUM row / value in Divisor column on SUM row.
    sumRow.ratioToNav = sumRow.notionalPortfolioCcy / sumRow.divisor;

    return cdsExposurePreview;
};

const calculateIssuerExposurePreview = (issuerExposure: IssuerExposure[], newTrades: number[], subscrValue: number): IssuerExposure[] => {
    const sumOfExposure: number = issuerExposure
        .filter(({ sumRow }) => sumRow === false)
        .reduce((acc, item: IssuerExposure) => acc + item.exposure, 0);

    // When calculating the percent then instead of dividing with the "sum of exposure" the denominator should be: (sum of exposure + subscription/redemption * 1 000 000)
    const denominator = subscrValue ? sumOfExposure + subscrValue * 1000000 : sumOfExposure;

    let sumPercent = 0;
    const issuerExposurePreview = issuerExposure.map((item, index) => {
        const preItem: IssuerExposure = cloneDeep(item);
        // add Subscr/Red
        preItem.subscRed = subscrValue;

        preItem.percent =
            newTrades.length > 0 && newTrades[index]
                ? (preItem.exposure + newTrades[index] * 1000000) / denominator // The percent value should be: (Exposure + input value * 1 000 000) / sum of exposure
                : subscrValue
                  ? preItem.exposure / denominator
                  : preItem.percent;

        if (
            preItem.percent > 0.05 &&
            !preItem.sumRow &&
            preItem.issuerType !== IssuerTypeEnum.Government &&
            preItem.issuer !== null &&
            preItem.issuerId !== "5c46ff650a40e100016db4d4" //"IHS Markit Benchmark Administration Limited"
        ) {
            sumPercent += preItem.percent;
        }

        return preItem;
    });

    const sumOver5Index = issuerExposurePreview.findIndex((row) => row.sumRow && row.issuer === "Sum over 5% ex gov, markit");

    issuerExposurePreview[sumOver5Index].percent = sumPercent;

    return issuerExposurePreview;
};

const calculateDurationRates: any = (data: TradingManagerColumn[], subRed: number, addedValuesDict: { [key: string]: number }) => {
    const nav = sumAll(data);

    const getDurationItem = (riskType: string, dv01: number) => {
        // Added DV01 = Input box
        const addedValue = addedValuesDict[riskType] ?? 0;
        // Duration = xxx DV01 SEK / NAV * 1e4
        const duration = (dv01 / nav) * 1e4;
        // Duration after sub/red = xxx DV01 SEK / (NAV+sub/red) * 1e4
        const durationAfter = (dv01 / (nav + subRed * 1e6)) * 1e4;
        // New duration = (xxx DV01 SEK + Added DV01) / (NAV+sub/red) * 1e4
        const newDuration = ((dv01 + addedValue) / (nav + subRed * 1e6)) * 1e4;

        return {
            riskType, // xxx = Either Rates, Credit (bonds), or Credit (other)
            dv01,
            duration,
            durationAfter,
            addedValue,
            newDuration
        };
    };

    // Rates DV01 SEK = Calculated from summing of column interestRateYieldDelta
    const sumInterestRateYieldDelta = data.reduce((acc, item) => acc + item.swaplab2Results.interestRateYieldDelta, 0);
    const ratesDuration = getDurationItem("Rates", sumInterestRateYieldDelta);

    // Credit (bonds) DV01 SEK = Calculated from summing of column creditYieldDelta for Bond instruments
    const sumCreditYieldDeltaBonds = data
        .filter((item) => item.modelType === InstrumentModelTypeEnum.Bond)
        .reduce((acc, item) => acc + item.swaplab2Results.creditYieldDelta, 0);
    const creditBondsDuration = getDurationItem("Credit (bonds)", sumCreditYieldDeltaBonds);

    // Credit (other) DV01 SEK = Calculated from summing of column creditYieldDelta for non-Bond instruments
    const sumCreditYieldDeltaOther = data
        .filter((item) => item.modelType !== InstrumentModelTypeEnum.Bond)
        .reduce((acc, item) => acc + item.swaplab2Results.creditYieldDelta, 0);
    const creditOtherDuration = getDurationItem("Credit (other)", sumCreditYieldDeltaOther);

    return [ratesDuration, creditBondsDuration, creditOtherDuration];
};

export function PreTrade({
    clientIds,
    endDate,
    lookThrough
}: {
    clientIds: string[];
    endDate: string;
    lookThrough: boolean;
}): React.ReactElement {
    const { id } = useParams<{ id: string }>();
    const previousId: string = usePrevious(id);
    const [subscrValue, setSubscrValue] = useState("");
    const [cdsExposureNewTrades, setCdsExposureNewTrades] = useState([]);
    const [issuerNewTrades, setIssuerNewTrades] = useState([]);
    const [durationAddedValues, setDurationAddedValues] = useState({ Rates: "", "Credit (bonds)": "", "Credit (other)": "" });
    const [importingOrders, setImportingOrders] = useState(false);
    const [ordersStatus, setOrdersStatus] = useState("");
    const client = useClient();

    const [{ fetching: loading, error: error, data: data }] = useQuery({
        query: getTradingmanager,
        variables: { clientIds: clientIds, endDate, lookThrough },
        requestPolicy: "network-only"
    });

    const [tradingmanager, setTradingmanager] = useState(null);

    useEffect(() => {
        if (data) {
            setTradingmanager(data.tradingmanager);
        } else if (id !== previousId) {
            setTradingmanager(null);
        }
    }, [data, id, previousId]);

    useEffect(() => {
        if (previousId && previousId !== id) {
            setCdsExposureNewTrades([]);
            setIssuerNewTrades([]);
            setSubscrValue("");
            setOrdersStatus("");
        }
    }, [id, previousId]);

    if (loading) return <div>Loading...</div>;
    if (error) return <p>error: {JSON.stringify(error, null, 2)}</p>;
    if (!tradingmanager) return <div>"No data found"</div>;

    // Due to hedge instrument has short maturity than risk position divisor is used to adjust the relation between notionals
    // const divisor_hedge = 5;
    const referenceTenor = 5;
    const issuerExposure = getIssuerExposure(tradingmanager);
    const modelTypeExposure = getModelTypeExposure(tradingmanager);
    const currencyExposure = getCurrencyExposure(tradingmanager);
    const [cdsExposure, esgExposure] = getcdsIndexExposure(tradingmanager, referenceTenor);

    issuerExposure.sort(function (a, b) {
        return b.exposure - a.exposure;
    });

    const cdsExposurePreview: CdsExposure[] = calculateCdsExposurePreview(
        cdsExposure,
        cdsExposureNewTrades,
        Number(subscrValue),
        esgExposure
    );

    const issuerExposurePreview: IssuerExposure[] = calculateIssuerExposurePreview(issuerExposure, issuerNewTrades, Number(subscrValue));

    const durationRecords = calculateDurationRates(data.tradingmanager, Number(subscrValue), durationAddedValues);

    // Calculate sequential tabIndex values for all inputs
    const issuerInputsCount = issuerExposurePreview.filter((item) => !item.sumRow).length;
    const cdsInputsCount = cdsExposurePreview.filter((item) => item.name !== "SUM").length;
    const issuerTabIndexStart = 1;
    const cdsTabIndexStart = issuerTabIndexStart + issuerInputsCount;
    const riskTabIndexStart = cdsTabIndexStart + cdsInputsCount;

    const importOrders = async () => {
        const fundId = clientIds[0];
        setImportingOrders(true);
        setOrdersStatus("Loading orders...");
        const result = await client
            .query(getFundOrders, { clientId: fundId, date: endDate }, { requestPolicy: "network-only" })
            .toPromise();
        setImportingOrders(false);
        if (result.error) {
            setOrdersStatus(`Could not load orders for ${endDate}: ${result.error.message}`);
            return;
        }
        const fund = result.data?.parties?.find((p) => p._id === fundId);
        if (!fund) {
            setOrdersStatus("(this client is not a fund, so it has no orders)");
            return;
        }
        const netted = nettedOrderValues(fund, result.data.transferAgentOrders ?? [], result.data.navs ?? []);
        if (netted.length === 0) {
            const hasSettledOrders = (result.data.transferAgentOrders ?? []).some(
                (o) => o.clientId === fundId && o.status === TransactionStatus.Settled
            );
            if (hasSettledOrders) {
                setOrdersStatus(`(orders on ${endDate} already in the balance, the NAV has been set)`);
            } else {
                setOrdersStatus(`(no orders on ${endDate})`);
            }
            return;
        }

        const baseCurrency: string = tradingmanager.length > 0 ? tradingmanager[0].baseCurrency : null;
        const fxRates: Record<string, number> = {};
        tradingmanager.forEach((row: TradingManagerColumn) => {
            if (row.currency && typeof row.fxRate === "number") {
                fxRates[row.currency] = row.fxRate;
            }
        });
        const { total, missingFxCurrencies } = nettedOrdersInBaseCurrency(netted, baseCurrency, fxRates);
        const millions = toWholeMillionsRoundedDown(total);
        setSubscrValue(String(millions));

        let status = `(orders on ${endDate}: ${baseCurrency} ${numberFormatFun("# ##0")(total)}, rounded down to ${millions} M)`;
        if (missingFxCurrencies.length > 0) {
            status = `${status} Orders in ${missingFxCurrencies.join(", ")} left out, no FX rate.`;
        }
        setOrdersStatus(status);
    };

    return (
        <Fragment>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 md:w-1/2">
                    <div className="flex form">
                        <DateForm defaultDateString={endDate} dateName="endDate"></DateForm>
                        <div className="mb-4 w-full ml-4">
                            <div className="row-col">
                                <label>Subscription/Redemption (M)</label>
                            </div>
                            <div className="row-col">
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="-?[0-9]*"
                                    className="form-input"
                                    style={{ width: "220px" }}
                                    value={subscrValue}
                                    onChange={(e) => {
                                        if (!e.target.value || e.target.value.length === 0) {
                                            setSubscrValue("");
                                            return false;
                                        }

                                        if (e.target.value == "-") {
                                            setSubscrValue("-");
                                            return false;
                                        }

                                        let value: any = Number(e.target.value);
                                        if (isNaN(value)) {
                                            value = "";
                                        }
                                        setSubscrValue(value);
                                    }}
                                />
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    type="button"
                                    className="ml-2"
                                    disabled={importingOrders}
                                    onClick={importOrders}
                                >
                                    Import fund orders
                                </Button>
                            </div>
                            {ordersStatus ? <div className="mt-1 text-gray-600">{ordersStatus}</div> : null}
                        </div>
                    </div>
                    <div>{!check_today_date(endDate) ? <Alert variant="danger">Historical or future date selected</Alert> : null}</div>
                </div>
                <div className="w-full px-2 md:w-1/2"></div>
            </div>

            <div className="flex flex-wrap -mx-2 mt-4">
                <div className="w-full px-2 md:w-1/2">
                    <Grid header="Issuers" data={issuerExposurePreview} sortable tableClassName="table-xs">
                        <Column
                            field="issuer"
                            className="nowrap maxwidth150px left"
                            format={(value, item) => {
                                return item && item.issuerId ? (
                                    <Link to={`/parties/${item.issuerId}`}>{value}</Link>
                                ) : (
                                    value
                                );
                            }}
                        />
                        <Column field="exposure" format={numberFormatFun("# ##0")} />
                        <Column
                            field="newTrade"
                            title="New trade (M)"
                            className="align-middle"
                            format={(_, item, index) => {
                                if (!item.sumRow) {
                                    // Calculate sequential index excluding sumRow items
                                    const inputIndex = issuerExposurePreview.slice(0, index).filter((i) => !i.sumRow).length;
                                    return (
                                        <div className="align-middle">
                                            <input
                                                type="number"
                                                tabIndex={issuerTabIndexStart + inputIndex}
                                                data-ndx={index}
                                                className="form-input"
                                                style={{ width: "80px", height: "22px" }}
                                                value={item.newTrade}
                                                onChange={(e) => {
                                                    const trades = issuerExposurePreview.map((_, index) => {
                                                        return issuerNewTrades[index] ? issuerNewTrades[index] : "";
                                                    });
                                                    trades[index] = e.target.value;
                                                    setIssuerNewTrades(trades);
                                                }}
                                            />
                                        </div>
                                    );
                                } else {
                                    return "";
                                }
                            }}
                        />
                        <Column field="percent" format={numberFormatFun("# ##0.00%")} />
                    </Grid>

                    <Grid header="Model types" data={modelTypeExposure} sortable tableClassName="table-xs">
                        <Column field="modelType" title="Model type" className="nowrap maxwidth150px left" />
                        <Column field="exposure" format={numberFormatFun("# ##0")} />
                        <Column field="percent" format={numberFormatFun("# ##0.00%")} />
                    </Grid>

                    <Grid header="Currencies" data={currencyExposure} sortable tableClassName="table-xs">
                        <Column field="currency" className="nowrap maxwidth150px left" />
                        <Column field="percentTotal" title="Percent total" format={numberFormatFun("# ##0.00%")} />
                        <Column field="localExposure" title="Local exposure" format={numberFormatFun("# ##0")} />
                        <Column field="percentAssets" title="Percent assets" format={numberFormatFun("# ##0.00%")} />
                        <Column field="cash" format={numberFormatFun("# ##0")} />
                        <Column field="percentCash" title="Percent cash" format={numberFormatFun("# ##0.00%")} />
                    </Grid>
                </div>

                <div className="w-full px-2 md:w-1/2">
                    <Grid header="CDS Exposure" data={cdsExposurePreview} sortable={false} tableClassName="table-xs">
                        <Column
                            field="name"
                            className="align-middle nowrap maxwidth150px left"
                            format={(value, item) => {
                                return item && item.instrumentId ? (
                                    <Link to={`/instruments/${item.instrumentId}`}>{value}</Link>
                                ) : (
                                    value
                                );
                            }}
                        />
                        <Column field="currency" className="align-middle nowrap maxwidth150px left" />
                        <Column field="fxRate" title="FX rate" className="align-middle nowrap maxwidth150px left" />
                        <Column field="notional" className="align-middle nowrap" format={numberFormatFun("# ##0")} />
                        <Column
                            field="newTrade"
                            title="New trade (M)"
                            className="align-middle"
                            format={(_, item, index) => {
                                if (item.name === "SUM") {
                                    return null;
                                }
                                // Calculate sequential index excluding SUM items
                                const inputIndex = cdsExposurePreview.slice(0, index).filter((i) => i.name !== "SUM").length;
                                return (
                                    <div className="align-middle">
                                        <input
                                            type="number"
                                            tabIndex={cdsTabIndexStart + inputIndex}
                                            data-ndx={index}
                                            className="form-input"
                                            style={{ width: "70px", height: "22px" }}
                                            value={item.newTrade}
                                            onChange={(e) => {
                                                const trades = cdsExposurePreview.map((_, index) => {
                                                    return cdsExposureNewTrades[index] ? cdsExposureNewTrades[index] : "";
                                                });
                                                trades[index] = e.target.value;
                                                setCdsExposureNewTrades(trades);
                                            }}
                                        />
                                    </div>
                                );
                            }}
                        />
                        <Column field="divisor" className="align-middle" format={numberFormatFun("# ##0")} />
                        <Column
                            title="Ntl port ccy"
                            field="notionalPortfolioCcy"
                            format={numberFormatFun("# ##0")}
                            className="align-middle nowrap"
                        />
                        <Column field="ratioToNav" title="Ratio to nav" className="align-middle" format={numberFormatFun("# ##0.00%")} />
                    </Grid>

                    <Grid header="CDS ESG Hedge" data={esgExposure} sortable tableClassName="table-xs">
                        <Column
                            field="cdsName"
                            title="Name"
                            className="nowrap maxwidth150px left"
                            format={(value, item) => {
                                return item && item.cdsInstrumentId ? (
                                    <Link to={`/instruments/${item.cdsInstrumentId}`}>{value}</Link>
                                ) : (
                                    value
                                );
                            }}
                        />
                        <Column field="currency" className="nowrap maxwidth150px left" />
                        <Column field="cdsPosition" title="CDS position" format={numberFormatFun("# ##0")} className="nowrap" />
                        <Column
                            field="hedgeName"
                            title="Hedge name"
                            className="nowrap maxwidth150px left"
                            format={(value, item) => {
                                return item && item.hedgeInstrumentId ? (
                                    <Link to={`/instruments/${item.hedgeInstrumentId}`}>{value}</Link>
                                ) : (
                                    value
                                );
                            }}
                        />
                        <Column field="hedgeNotional" title="Notional" format={numberFormatFun("# ##0")} className="nowrap" />
                        <Column field="dirtyNoNames" title="Dirty #" />
                        <Column field="indexNoNames" title="Index #" />
                        <Column
                            field="grossDirtyExposure"
                            title={<TooltipLabel text={"Gross dirty"} content={"Gross dirty exposure. Positive sign means exposure."} />}
                            format={numberFormatFun("# ##0")}
                            className="nowrap"
                        />
                        <Column
                            field="netDirtyExposure"
                            title={
                                <TooltipLabel
                                    text={"Net dirty"}
                                    content={"Net dirty exposure. Negative sign means no exposure and overhedged."}
                                />
                            }
                            format={numberFormatFun("# ##0")}
                            className="nowrap"
                        />
                    </Grid>

                    <Grid header="Risk" data={durationRecords} sortable tableClassName="table-xs">
                        <Column field="riskType" title="Risk type" className="nowrap" />
                        <Column field="dv01" title="DV01 SEK" format={numberFormatFun("# ##0")} className="nowrap" />
                        <Column field="duration" title="Duration" format={numberFormatFun("# ##0.00")} className="nowrap" />
                        <Column
                            field="durationAfter"
                            title="Duration after sub/red"
                            format={numberFormatFun("# ##0.00")}
                            className="nowrap"
                        />
                        <Column
                            field="addedValue"
                            title="Add/reduce DV01"
                            className="align-middle"
                            format={(_, item, index) => {
                                return (
                                    <div className="align-middle">
                                        <input
                                            type="number"
                                            tabIndex={riskTabIndexStart + index}
                                            className="form-input"
                                            style={{ width: "90px", height: "22px" }}
                                            value={item.addedValue}
                                            onChange={(e) => {
                                                const newValues = { ...durationAddedValues };
                                                newValues[item.riskType] = e.target.value ? Number(e.target.value) : "";
                                                setDurationAddedValues(newValues);
                                            }}
                                        />
                                    </div>
                                );
                            }}
                        />
                        <Column field="newDuration" title="New duration" format={numberFormatFun("# ##0.00")} />
                    </Grid>
                </div>
            </div>
        </Fragment>
    );
}
